import {FC} from "react"
import {ReactSVG} from "react-svg"

import imageIcon from "@frontend/design/icons/image.svg"
import {useTranslation} from "@frontend/i18n"
import {trackEvent} from "@frontend/utils/trackEvent"
import {ASK_FOR_MODEL_NUMBER, CaseEvent, Message} from "@ri2/db/client"
import {css, cx} from "@styled-system/css"
import {hstack, vstack} from "@styled-system/patterns"

import {MultipleChoiceOptions} from "./MultipleChoicesOptions"
import {NewMessageButton} from "./NewMessageButton"

// Number of pixels before the quick responses are fully invisible
const PIXEL_THRESHOLD_FOR_HIDING_QUICK_RESPONSE = 70

/**
 * Primary signal: the widget is mounted in slideout mode, which hosts only
 * embed on desktop layouts. Secondary signal (when not slideout): a touch
 * heuristic, used as a best-effort fallback.
 */
const isDesktopDevice = (): boolean => {
  if (typeof window !== "undefined" && window.ri2?.displayMode === "slideout") {
    return true
  }
  if (typeof navigator === "undefined") return true
  const hasTouch =
    (typeof navigator.maxTouchPoints === "number" &&
      navigator.maxTouchPoints > 0) ||
    /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent ?? "")
  return !hasTouch
}

const buildCaseShareUrl = (caseId: string): string =>
  `${window.location.origin}${window.location.pathname}${window.location.search}#ri2-case=${caseId}`

const ShareIcon: FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2" />
    <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2" />
    <line
      x1="8.59"
      x2="15.42"
      y1="13.51"
      y2="17.49"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="15.41"
      x2="8.59"
      y1="6.51"
      y2="10.49"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const ConversationActions: FC<{
  events: CaseEvent[]
  onChooseMultipleChoiceOption: (option: string) => void
  hasNewMessage: boolean
  scrollToBottom: (scrollToTopOfLatestMessage?: boolean) => void
  scrollUpSize: number
  compact?: boolean
  /** Called when the user clicks the "Upload a photo" quick action. */
  onUploadPhoto?: () => void
  /** Current case id, used to build the shareable deep link. */
  caseId?: string | null
}> = ({
  events,
  onChooseMultipleChoiceOption,
  hasNewMessage,
  scrollToBottom,
  scrollUpSize,
  compact = false,
  onUploadPhoto,
  caseId,
}) => {
  const t = useTranslation()
  const lastAiMessage = ((): Message | null => {
    const lastEvent = events[events.length - 1]
    if (lastEvent?.eventType === "message" && lastEvent.type === "ai") {
      return lastEvent
    }
    return null
  })()
  const isAskingForModelNumber =
    lastAiMessage?.type === "ai" && lastAiMessage.mode === ASK_FOR_MODEL_NUMBER

  const rawMultipleChoiceOptions =
    getMultipleChoiceOptionsFromMessage(lastAiMessage)

  // At the model-number step we already render two predefined client-side
  // pills (Upload photo + Send to my phone / Continue later). When the LLM
  // returns exactly two suggestions it's almost always filler ("Yes" / "No"
  // style pairs that duplicate the predefined actions), so trim to one to
  // keep the row focused. If it returns one, or three or more, the extras
  // tend to carry genuinely useful info worth keeping.
  const multipleChoiceOptions =
    isAskingForModelNumber &&
    rawMultipleChoiceOptions &&
    rawMultipleChoiceOptions.length === 2
      ? rawMultipleChoiceOptions.slice(0, 1)
      : rawMultipleChoiceOptions

  const hasMultipleChoiceOptions = !!multipleChoiceOptions?.length

  const opacity =
    scrollUpSize <= PIXEL_THRESHOLD_FOR_HIDING_QUICK_RESPONSE
      ? 100 - (100 * scrollUpSize) / PIXEL_THRESHOLD_FOR_HIDING_QUICK_RESPONSE
      : 0

  const isMultipleChoicesFadeOut = opacity === 0

  const isDesktop = isDesktopDevice()
  const continueLaterLabel = isDesktop
    ? t("conversation.sendToMyPhone")
    : t("conversation.continueLater")

  const canShareCase =
    typeof navigator !== "undefined" && typeof navigator.share === "function"

  const onContinueLater = async (): Promise<void> => {
    if (!caseId) return

    const eventName = compact
      ? "widget_chat_continue_later_click"
      : "chat_continue_later_click"
    trackEvent(eventName, caseId)

    const url = buildCaseShareUrl(caseId)

    if (canShareCase) {
      try {
        await navigator.share({url})
        return
      } catch {
        // User cancelled the share sheet or sharing failed — fall through
        // to clipboard so the action still does something useful.
      }
    }

    try {
      await navigator.clipboard?.writeText(url)
    } catch {
      // Clipboard access can fail in insecure contexts; nothing else to do.
    }
  }

  const continueLaterPill =
    isAskingForModelNumber && caseId ? (
      <button
        className={css({
          display: "flex",
          alignItems: "center",
          gap: 6,
          borderRadius: compact ? "20px 20px 4px 20px" : "24px 24px 4px 24px",
          borderWidth: compact ? 1.5 : 2,
          borderColor: "border.brand.primary",
          color: "text.brand.primary",
          paddingY: compact ? 7 : 10,
          paddingX: compact ? 12 : 16,
          backgroundColor: "background.brand.primarySubtle",
          textStyle: compact ? "bodyMedium" : "bodyLargeMedium",
          cursor: "pointer",
        })}
        onClick={() => {
          void onContinueLater()
        }}
      >
        <ShareIcon />
        {continueLaterLabel}
      </button>
    ) : null

  const uploadPhotoPill =
    isAskingForModelNumber && onUploadPhoto ? (
      <button
        className={css({
          display: "flex",
          alignItems: "center",
          gap: 6,
          borderRadius: compact ? "20px 20px 4px 20px" : "24px 24px 4px 24px",
          borderWidth: compact ? 1.5 : 2,
          borderColor: "border.brand.primary",
          color: "text.brand.primary",
          paddingY: compact ? 7 : 10,
          paddingX: compact ? 12 : 16,
          backgroundColor: "background.brand.primarySubtle",
          textStyle: compact ? "bodyMedium" : "bodyLargeMedium",
          cursor: "pointer",
        })}
        onClick={() => {
          if (compact) {
            trackEvent("widget_chat_upload_photo_click")
          } else {
            trackEvent("chat_upload_photo_click")
          }
          onUploadPhoto()
        }}
      >
        <ReactSVG
          src={imageIcon}
          className={css({
            "& svg": {
              width: 18,
              height: 18,
            },
            "& path": {
              fill: "currentColor",
            },
          })}
        />
        {t("conversation.uploadModelNumberPhoto")}
      </button>
    ) : null

  const hasQuickActions =
    hasMultipleChoiceOptions || !!uploadPhotoPill || !!continueLaterPill

  return (
    <>
      <div
        className={vstack({
          width: "100%",
          alignItems: "flex-end",
          position: "sticky",
          gap: 6,
          bottom: {
            base: -1,
            desktop: 0,
          },
          left: 0,
          right: 0,
          pointerEvents: !isMultipleChoicesFadeOut ? "auto" : "none",
        })}
      >
        {hasNewMessage && isMultipleChoicesFadeOut && (
          <div
            className={cx(
              hstack({
                justifyContent: "center",
                width: "100%",
              }),
              hasQuickActions
                ? css({
                    position: "absolute",
                    bottom: 32,
                  })
                : css({
                    marginBottom: 32,
                  }),
            )}
          >
            <NewMessageButton scrollToBottom={() => scrollToBottom(false)} />
          </div>
        )}
        {isMultipleChoicesFadeOut && (
          <div
            className={css({
              height: 32,
              position: "absolute",
              bottom: -1,
              width: "100%",
              bgGradient: "conversationFade",
            })}
          />
        )}
        {hasQuickActions && (
          <div
            style={{
              opacity: `${opacity}%`,
              padding: "0px 0px 16px 0px",
              pointerEvents: !isMultipleChoicesFadeOut ? "auto" : "none",
            }}
            className={vstack({
              alignItems: "flex-end",
              gap: 6,
            })}
          >
            <MultipleChoiceOptions
              options={multipleChoiceOptions ?? []}
              onChooseOption={onChooseMultipleChoiceOption}
              compact={compact}
              leadingAction={
                <>
                  {uploadPhotoPill}
                  {continueLaterPill}
                </>
              }
            />
          </div>
        )}
      </div>
    </>
  )
}

const getMultipleChoiceOptionsFromMessage = (
  message: Message | null,
): string[] | undefined =>
  message && message.type === "ai"
    ? message.content?.multipleChoiceAnswers?.map((answer) =>
        typeof answer === "string" ? answer : answer.text,
      )
    : undefined
